import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Paper,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Collapse,
  Chip,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Stack,
  Autocomplete,
} from '@mui/material';
import {
  Add,
  Search,
  ExpandMore,
  ExpandLess,
  Delete,
  Edit,
  PictureAsPdf,
  Image,
  VideoLibrary,
  Close,
  Description,
  CheckCircle,
  Warning,
  Lock,
  LockOpen,
  Save,
} from '@mui/icons-material';
import { formatDateTimeBr } from '../utils/datetime';
import { useAuth } from '../contexts/AuthContext';

export default function RecebimentoOSContent({ open, API, token }) {
  const { hasPermission, user } = useAuth();
  const normalizeAccess = (value) =>
    (value || '')
      .toString()
      .trim()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase();
  const accessCandidates = [
    user?.nivel_acesso,
    user?.nivel,
    user?.role,
    user?.nivelAcesso,
    user?.tipo,
    user?.perfil,
  ];
  const isManutencao = accessCandidates
    .map((value) => normalizeAccess(value))
    .some((value) => value.includes('manut'));
  const [view, setView] = useState('list'); // 'list' or 'create'
  const [osList, setOsList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Novos filtros
  const [filterClienteId, setFilterClienteId] = useState('');
  const [filterDataInicio, setFilterDataInicio] = useState('');
  const [filterDataFim, setFilterDataFim] = useState('');
  const [filterDivergencia, setFilterDivergencia] = useState('todos'); // 'todos', 'ok', 'divergente'
  const [filterStatusPedido, setFilterStatusPedido] = useState('abertos'); // 'todos', 'abertos', 'fechados'
  const [loadingFechar, setLoadingFechar] = useState(null);

  const [nfeInputs, setNfeInputs] = useState({});
  const [clients, setClients] = useState([]);

  // State for Create/Edit
  const [newOS, setNewOS] = useState({
    id: null,
    cliente_id: '',
    numero_os: '',
    descricao: '',
    status: 'pendente',
    foto: null,
    pdf: null,
    current_foto: null,
    current_pdf: null,
  });

  const [expandedId, setExpandedId] = useState(null);
  const [historyData, setHistoryData] = useState({});
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [despesasOS, setDespesasOS] = useState([]);

  // Estados para múltiplas notas fiscais
  const [notasInputs, setNotasInputs] = useState({}); // {rowId: {nfe: '', valor: ''}}
  const [loadingAddNota, setLoadingAddNota] = useState(null);
  const [notasLocais, setNotasLocais] = useState({}); // {notaId: {numero_nfe, valor_nfe, nota_divergente}}
  const [savingNota, setSavingNota] = useState(null);

  // Sincronizar notas locais quando osList muda
  useEffect(() => {
    setNotasLocais((prev) => {
      const newNotasLocais = {};
      osList.forEach((os) => {
        (os.notas_fiscais || []).forEach((nota) => {
          if (!prev[nota.id]) {
            newNotasLocais[nota.id] = {
              numero_nfe: nota.numero_nfe || '',
              valor_nfe: nota.valor_nfe || '',
              nota_divergente: nota.nota_divergente || false,
            };
          }
        });
      });
      return Object.keys(newNotasLocais).length > 0 ? { ...prev, ...newNotasLocais } : prev;
    });
  }, [osList]);

  // Estados para visualizadores de mídia
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerType, setViewerType] = useState(null); // 'foto', 'pdf', 'video'
  const [viewerUrl, setViewerUrl] = useState('');
  const [viewerTitle, setViewerTitle] = useState('');

  // Estados para valores e divergência
  const [valorInputs, setValorInputs] = useState({}); // valor_nfe por row.id
  const [valorPedidoInfo, setValorPedidoInfo] = useState({}); // valor do pedido por row.id
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingReceber, setPendingReceber] = useState(null); // {id, nfe, valor_nfe}

  // Buscar OS cadastradas em Despesas para o cliente selecionado
  const fetchDespesasOS = useCallback(
    async (clienteId) => {
      if (!clienteId) {
        setDespesasOS([]);
        return;
      }
      try {
        const res = await fetch(`${API}/despesas/os_por_cliente?cliente_id=${clienteId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setDespesasOS(data);
        } else {
          setDespesasOS([]);
        }
      } catch (e) {
        console.error('Erro ao buscar OS de despesas:', e);
        setDespesasOS([]);
      }
    },
    [API, token]
  );

  const fetchOSList = useCallback(async (clienteId) => {
    if (!clienteId) return;
    try {
      const url =
        clienteId === 'all'
          ? `${API}/recebimento-os/`
          : `${API}/recebimento-os/?cliente_id=${clienteId}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        data.sort((a, b) => new Date(b.criado_em) - new Date(a.criado_em));
        setOsList(data);
      }
    } catch (error) {
      console.error('Error fetching OS list:', error);
    }
  }, [API, token]);

  const fetchClients = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      let clienteIds = null;
      try {
        const meResponse = await fetch(`${API}/me/clientes`, { headers });
        if (meResponse.ok) {
          const meData = await meResponse.json();
          clienteIds = meData.clientes || [];
        }
      } catch (e) {
        console.warn('Endpoint /me/clientes não disponível');
      }
      const res = await fetch(`${API}/clientes/`, { headers });
      if (res.ok) {
        let data = await res.json();
        if (clienteIds && clienteIds.length > 0) {
          data = data.filter((c) => clienteIds.map(Number).includes(Number(c.id)));
        }
        setClients(data);
        // Auto-seleciona se o usuário tem acesso a apenas 1 cliente
        if (data.length === 1) {
          setFilterClienteId(String(data[0].id));
        }
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  }, [API, token]);

  // Carrega OS quando cliente é selecionado (não auto-carrega ao abrir)
  useEffect(() => {
    if (open) {
      setOsList([]);
      fetchClients();
    }
  }, [open, fetchClients]);

  useEffect(() => {
    if (!open || !filterClienteId) return;
    fetchOSList(filterClienteId);
  }, [open, filterClienteId, fetchOSList]);

  useEffect(() => {
    let result = osList;

    if (searchQuery) {
      const lower = searchQuery.toLowerCase();
      result = result.filter(
        (item) =>
          item.numero_os.toLowerCase().includes(lower) ||
          (item.cliente?.nome || '').toLowerCase().includes(lower)
      );
    }

    if (filterDataInicio) {
      const inicio = new Date(filterDataInicio);
      inicio.setHours(0, 0, 0, 0);
      result = result.filter((item) => new Date(item.criado_em) >= inicio);
    }

    if (filterDataFim) {
      const fim = new Date(filterDataFim);
      fim.setHours(23, 59, 59, 999);
      result = result.filter((item) => new Date(item.criado_em) <= fim);
    }

    if (filterDivergencia === 'divergente') {
      result = result.filter(
        (item) =>
          item.nota_divergente ||
          item.tem_nota_divergente ||
          (item.notas_fiscais && item.notas_fiscais.some((n) => n.nota_divergente))
      );
    } else if (filterDivergencia === 'ok') {
      result = result.filter(
        (item) =>
          !item.nota_divergente &&
          !item.tem_nota_divergente &&
          (!item.notas_fiscais || item.notas_fiscais.every((n) => !n.nota_divergente))
      );
    }

    if (filterStatusPedido === 'abertos') {
      result = result.filter((item) => item.status !== 'fechado');
    } else if (filterStatusPedido === 'fechados') {
      result = result.filter((item) => item.status === 'fechado');
    }

    setFilteredList(result);
  }, [searchQuery, filterDataInicio, filterDataFim, filterDivergencia, filterStatusPedido, osList]);

  // Paginação
  const ITEMS_PER_PAGE = 30;
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(filteredList.length / ITEMS_PER_PAGE);
  const filteredListPaginada = useMemo(
    () => filteredList.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE),
    [filteredList, page]
  );

  // Resetar página ao mudar filtros
  useEffect(() => {
    setPage(1);
  }, [searchQuery, filterClienteId, filterDataInicio, filterDataFim, filterDivergencia, filterStatusPedido]);

  const fetchHistory = async (id) => {
    setLoadingHistory(true);
    try {
      const res = await fetch(`${API}/recebimento-os/${id}/historico`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setHistoryData((prev) => ({ ...prev, [id]: data }));
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleSaveOS = async () => {
    if (!newOS.cliente_id || !newOS.numero_os) {
      alert('Preencha Cliente e Número da OS');
      return;
    }
    try {
      const formData = new FormData();
      formData.append('cliente_id', newOS.cliente_id);
      formData.append('numero_os', newOS.numero_os);
      formData.append('descricao', newOS.descricao || '');
      if (newOS.status) formData.append('status', newOS.status);
      if (newOS.foto) formData.append('foto', newOS.foto);
      if (newOS.pdf) formData.append('pdf', newOS.pdf);

      const url = newOS.id ? `${API}/recebimento-os/${newOS.id}` : `${API}/recebimento-os/`;

      const method = newOS.id ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method: method,
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (res.ok) {
        alert(newOS.id ? 'Pedido atualizado!' : 'Pedido cadastrado!');
        setView('list');
        setNewOS({
          id: null,
          cliente_id: '',
          numero_os: '',
          descricao: '',
          status: 'pendente',
          foto: null,
          pdf: null,
          current_foto: null,
          current_pdf: null,
        });
        fetchOSList(filterClienteId);
      } else {
        const text = await res.text();
        let detail = 'Falha na operação';
        try {
          const err = JSON.parse(text);
          detail = err.detail || err.error || detail;
        } catch {
          if (text) detail = text;
        }
        alert(`Erro: ${detail}`);
      }
    } catch (error) {
      console.error('Error saving OS:', error);
      alert('Erro ao conectar com o servidor.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este pedido?')) return;
    try {
      const res = await fetch(`${API}/recebimento-os/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        alert('Pedido excluído!');
        fetchOSList(filterClienteId);
      } else {
        const err = await res.json();
        alert(`Erro: ${err.detail || 'Falha ao excluir'}`);
      }
    } catch (error) {
      console.error('Error deleting OS:', error);
    }
  };

  const handleEdit = (row) => {
    setNewOS({
      id: row.id,
      cliente_id: row.cliente_id,
      numero_os: row.numero_os,
      descricao: row.descricao || '',
      status: row.status || (row.data_recebimento ? 'recebido' : 'pendente'),
      foto: null,
      pdf: null,
      current_foto: row.foto_path,
      current_pdf: row.pdf_path,
    });
    setView('create');
  };

  // Fechar pedido
  const handleFecharPedido = async (id) => {
    if (
      !window.confirm(
        'Deseja fechar este pedido? Após fechado, ele não aparecerá mais na listagem padrão.'
      )
    )
      return;
    setLoadingFechar(id);
    try {
      const formData = new FormData();
      formData.append('status', 'fechado');
      const res = await fetch(`${API}/recebimento-os/${id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) {
        fetchOSList(filterClienteId);
      } else {
        alert('Erro ao fechar pedido');
      }
    } catch (error) {
      console.error('Erro ao fechar pedido:', error);
    } finally {
      setLoadingFechar(null);
    }
  };

  // Reabrir pedido
  const handleReabrirPedido = async (id) => {
    if (!window.confirm('Deseja reabrir este pedido?')) return;
    try {
      const formData = new FormData();
      formData.append('status', 'recebido');
      const res = await fetch(`${API}/recebimento-os/${id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) {
        alert('Pedido reaberto!');
        fetchOSList(filterClienteId);
        setView('list');
      } else {
        alert('Erro ao reabrir pedido');
      }
    } catch (error) {
      console.error('Erro ao reabrir pedido:', error);
    }
  };

  const handleReceber = async (id, forceConfirm = false) => {
    const nfe = nfeInputs[id];
    if (!nfe) return;

    const valorNfe = valorInputs[id] ? parseFloat(valorInputs[id].replace(',', '.')) : null;
    const valorPedido = valorPedidoInfo[id] ? parseFloat(valorPedidoInfo[id]) : null;

    // Se tem valores para comparar e não forçou confirmação, verificar divergência
    if (!forceConfirm && valorNfe !== null && valorPedido !== null) {
      const isDivergente = Math.abs(valorNfe - valorPedido) > 0.01;
      if (isDivergente) {
        setPendingReceber({ id, nfe, valor_nfe: valorNfe, valor_pedido: valorPedido });
        setConfirmDialogOpen(true);
        return;
      }
    }

    try {
      const formData = new FormData();
      formData.append('numero_nfe', nfe);
      if (valorNfe !== null) {
        formData.append('valor_nfe', valorNfe);
      }
      if (valorPedido !== null) {
        formData.append('valor_pedido', valorPedido);
      }
      // Verificar divergência
      const notaDivergente =
        valorNfe !== null && valorPedido !== null && Math.abs(valorNfe - valorPedido) > 0.01;
      formData.append('nota_divergente', notaDivergente ? 'true' : 'false');

      const res = await fetch(`${API}/recebimento-os/${id}/receber`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (res.ok) {
        alert(
          notaDivergente
            ? 'Recebimento confirmado (Nota Divergente)!'
            : 'Recebimento confirmado com sucesso!'
        );
        setNfeInputs((prev) => ({ ...prev, [id]: '' }));
        setValorInputs((prev) => ({ ...prev, [id]: '' }));
        fetchOSList(filterClienteId);
        if (expandedId === id) {
          fetchHistory(id);
        }
      } else {
        alert('Erro ao confirmar recebimento.');
      }
    } catch (error) {
      console.error('Error receiving OS:', error);
      alert('Erro ao conectar com o servidor.');
    }
  };

  const handleCancelDivergente = () => {
    setConfirmDialogOpen(false);
    setPendingReceber(null);
  };

  // === FUNÇÕES PARA MÚLTIPLAS NOTAS FISCAIS ===

  const handleAddNota = async (osId, forceConfirm = false) => {
    const input = notasInputs[osId] || {};
    const nfe = input.nfe;
    const valorStr = input.valor;

    if (!nfe) {
      alert('Informe o número da NFE');
      return;
    }

    const valorNfe = valorStr ? parseFloat(valorStr.replace(',', '.')) : null;
    const valorPedido = valorPedidoInfo[osId] ? parseFloat(valorPedidoInfo[osId]) : null;

    // Verificar divergência antes de confirmar
    if (!forceConfirm && valorNfe !== null && valorPedido !== null) {
      const isDivergente = Math.abs(valorNfe - valorPedido) > 0.01;
      if (isDivergente) {
        setPendingReceber({
          id: osId,
          nfe,
          valor_nfe: valorNfe,
          valor_pedido: valorPedido,
          isMultiNota: true,
        });
        setConfirmDialogOpen(true);
        return;
      }
    }

    setLoadingAddNota(osId);
    try {
      const formData = new FormData();
      formData.append('numero_nfe', nfe);
      if (valorNfe !== null) formData.append('valor_nfe', valorNfe);
      if (valorPedido !== null) formData.append('valor_pedido', valorPedido);
      // Enviar status de divergência (se selecionado manualmente)
      if (input.divergente !== undefined) {
        formData.append('nota_divergente', input.divergente);
      }

      const res = await fetch(`${API}/recebimento-os/${osId}/notas`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (res.ok) {
        // Limpar inputs
        setNotasInputs((prev) => ({ ...prev, [osId]: { nfe: '', valor: '', divergente: false } }));
        fetchOSList(filterClienteId); // Atualizar lista para mostrar nova nota
      } else {
        const err = await res.json();
        alert(`Erro: ${err.error || 'Falha ao adicionar nota'}`);
      }
    } catch (error) {
      console.error('Erro ao adicionar nota:', error);
      alert('Erro ao conectar com o servidor.');
    } finally {
      setLoadingAddNota(null);
    }
  };

  const handleNotaLocalChange = (notaId, field, value) => {
    setNotasLocais((prev) => ({
      ...prev,
      [notaId]: {
        ...prev[notaId],
        [field]: value,
      },
    }));
  };

  const handleSaveNota = async (osId, notaId) => {
    const edits = notasLocais[notaId];
    if (!edits) return;

    setSavingNota(notaId);
    try {
      const res = await fetch(`${API}/recebimento-os/${osId}/notas/${notaId}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          numero_nfe: edits.numero_nfe,
          valor_nfe: edits.valor_nfe,
          nota_divergente: edits.nota_divergente,
        }),
      });

      if (res.ok) {
        fetchOSList(filterClienteId);
      } else {
        alert('Erro ao salvar nota');
      }
    } catch (error) {
      console.error('Erro ao salvar nota:', error);
      alert('Erro ao salvar nota');
    } finally {
      setSavingNota(null);
    }
  };

  const handleRemoveNota = async (osId, notaId) => {
    if (!window.confirm('Excluir esta nota fiscal?')) return;

    try {
      const res = await fetch(`${API}/recebimento-os/${osId}/notas/${notaId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        fetchOSList(filterClienteId);
      } else {
        alert('Erro ao excluir nota');
      }
    } catch (error) {
      console.error('Erro ao excluir nota:', error);
    }
  };

  // Atualizar handleConfirmDivergente para suportar múltiplas notas
  const handleConfirmDivergenteMulti = () => {
    if (pendingReceber) {
      if (pendingReceber.isMultiNota) {
        handleAddNota(pendingReceber.id, true);
      } else {
        handleReceber(pendingReceber.id, true);
      }
    }
    setConfirmDialogOpen(false);
    setPendingReceber(null);
  };

  const toggleExpand = (id) => {
    if (expandedId === id) {
      setExpandedId(null);
    } else {
      setExpandedId(id);
      if (!historyData[id]) {
        fetchHistory(id);
      }
      // Buscar valor do pedido da despesa vinculada
      fetchValorPedido(id);
    }
  };

  // Buscar valor do pedido da despesa vinculada
  const fetchValorPedido = async (id) => {
    const row = osList.find((r) => r.id === id);
    if (!row) return;
    try {
      const res = await fetch(`${API}/recebimento-os/${id}/despesa-anexos`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.valor_pedido_despesa) {
          setValorPedidoInfo((prev) => ({ ...prev, [id]: data.valor_pedido_despesa }));
        }
      }
    } catch (e) {
      console.error('Erro ao buscar valor do pedido:', e);
    }
  };

  // Função para abrir visualizador
  const openViewer = (type, url, title = '') => {
    setViewerType(type);
    setViewerUrl(url);
    setViewerTitle(title);
    setViewerOpen(true);
  };

  const closeViewer = () => {
    setViewerOpen(false);
    setViewerType(null);
    setViewerUrl('');
    setViewerTitle('');
  };

  const handleFileChange = (e, type) => {
    if (e.target.files && e.target.files[0]) {
      setNewOS({ ...newOS, [type]: e.target.files[0] });
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h6">Recebimento de OS / NFE</Typography>
        {view === 'list' && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setNewOS({
                id: null,
                cliente_id: '',
                numero_os: '',
                descricao: '',
                foto: null,
                pdf: null,
                current_foto: null,
                current_pdf: null,
              });
              setView('create');
            }}
          >
            Cadastro NFE
          </Button>
        )}
        {view === 'create' && (
          <Button variant="outlined" onClick={() => setView('list')}>
            Voltar para Lista
          </Button>
        )}
      </Box>

      {view === 'create' ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Cliente</InputLabel>
            <Select
              value={newOS.cliente_id}
              label="Cliente"
              onChange={(e) => {
                const cId = e.target.value;
                setNewOS({ ...newOS, cliente_id: cId, numero_os: '' });
                fetchDespesasOS(cId);
              }}
            >
              {clients.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.nome}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Autocomplete
            freeSolo
            options={despesasOS.map((d) => d.numero_os)}
            value={newOS.numero_os}
            onInputChange={(_, value) => setNewOS({ ...newOS, numero_os: value })}
            renderOption={(props, option) => {
              const item = despesasOS.find((d) => d.numero_os === option);
              return (
                <li {...props} key={option}>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {option}
                    </Typography>
                    {item?.servico && (
                      <Typography variant="caption" color="text.secondary">
                        {item.servico}
                      </Typography>
                    )}
                  </Box>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Número da OS / Pedido"
                fullWidth
                helperText={
                  despesasOS.length > 0
                    ? `${despesasOS.length} OS cadastrada(s) em Despesas para esta loja`
                    : ''
                }
              />
            )}
          />
          <TextField
            label="Dados do Pedido / Descrição"
            fullWidth
            multiline
            rows={3}
            value={newOS.descricao}
            onChange={(e) => setNewOS({ ...newOS, descricao: e.target.value })}
          />

          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={newOS.status}
              label="Status"
              onChange={(e) => setNewOS({ ...newOS, status: e.target.value })}
              disabled={isManutencao}
            >
              <MenuItem value="pendente">Pendente</MenuItem>
              <MenuItem value="em_andamento">Em andamento</MenuItem>
              <MenuItem value="recebido">Recebido</MenuItem>
            </Select>
          </FormControl>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Button variant="outlined" component="label" fullWidth startIcon={<Image />}>
                {newOS.foto ? newOS.foto.name : 'Adicionar Foto'}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, 'foto')}
                />
              </Button>
              {newOS.current_foto && !newOS.foto && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Foto atual:{' '}
                  <a
                    href={
                      newOS.current_foto?.startsWith('/')
                        ? newOS.current_foto
                        : `/${newOS.current_foto}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Visualizar
                  </a>
                </Typography>
              )}
            </Grid>
            <Grid item xs={6}>
              <Button variant="outlined" component="label" fullWidth startIcon={<PictureAsPdf />}>
                {newOS.pdf ? newOS.pdf.name : 'Adicionar PDF'}
                <input
                  type="file"
                  hidden
                  accept="application/pdf"
                  onChange={(e) => handleFileChange(e, 'pdf')}
                />
              </Button>
              {newOS.current_pdf && !newOS.pdf && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  PDF atual:{' '}
                  <a
                    href={
                      newOS.current_pdf?.startsWith('/')
                        ? newOS.current_pdf
                        : `/${newOS.current_pdf}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Visualizar
                  </a>
                </Typography>
              )}
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
            <Button variant="contained" color="primary" onClick={handleSaveOS}>
              {newOS.id ? 'Atualizar' : 'Salvar'}
            </Button>
            {/* Botão Reabrir na edição se o pedido estiver fechado */}
            {newOS.id && newOS.status === 'fechado' && (
              <Button
                variant="outlined"
                color="primary"
                onClick={() => handleReabrirPedido(newOS.id)}
                startIcon={<LockOpen />}
              >
                Reabrir Pedido
              </Button>
            )}
          </Box>
        </Box>
      ) : (
        <Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
            <Box sx={{ flex: '1 1 200px' }}>
              <TextField
                label="Buscar OS"
                variant="outlined"
                size="small"
                fullWidth
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  endAdornment: <Search color="action" />,
                }}
              />
            </Box>
            <Box sx={{ flex: '1 1 200px' }}>
              <FormControl fullWidth size="small" required
                sx={!filterClienteId ? { '& .MuiOutlinedInput-notchedOutline': { borderColor: 'warning.main', borderWidth: 2 } } : {}}>
                <InputLabel>Cliente *</InputLabel>
                <Select
                  value={filterClienteId}
                  label="Cliente *"
                  onChange={(e) => setFilterClienteId(e.target.value)}
                >
                  <MenuItem value="">Selecione...</MenuItem>
                  <MenuItem value="all">TODOS</MenuItem>
                  {clients.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.nome}
                    </MenuItem>
                  ))}
                </Select>
                {!filterClienteId && (
                  <Typography variant="caption" sx={{ color: 'warning.dark', mt: 0.5, px: 1 }}>
                    Selecione um cliente para carregar
                  </Typography>
                )}
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 140px' }}>
              <TextField
                type="date"
                label="Data Início"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={filterDataInicio}
                onChange={(e) => setFilterDataInicio(e.target.value)}
              />
            </Box>
            <Box sx={{ flex: '1 1 140px' }}>
              <TextField
                type="date"
                label="Data Fim"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={filterDataFim}
                onChange={(e) => setFilterDataFim(e.target.value)}
              />
            </Box>
            <Box sx={{ flex: '1 1 150px' }}>
              <FormControl fullWidth size="small">
                <InputLabel>Status Nota</InputLabel>
                <Select
                  value={filterDivergencia}
                  label="Status Nota"
                  onChange={(e) => setFilterDivergencia(e.target.value)}
                >
                  <MenuItem value="todos">Todos</MenuItem>
                  <MenuItem value="ok">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CheckCircle fontSize="small" color="success" />
                      <span>OK</span>
                    </Box>
                  </MenuItem>
                  <MenuItem value="divergente">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Warning fontSize="small" color="error" />
                      <span>Divergente</span>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 140px' }}>
              <FormControl fullWidth size="small">
                <InputLabel>Status Pedido</InputLabel>
                <Select
                  value={filterStatusPedido}
                  label="Status Pedido"
                  onChange={(e) => setFilterStatusPedido(e.target.value)}
                >
                  <MenuItem value="abertos">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LockOpen fontSize="small" color="primary" />
                      <span>Abertos</span>
                    </Box>
                  </MenuItem>
                  <MenuItem value="fechados">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Lock fontSize="small" color="action" />
                      <span>Fechados</span>
                    </Box>
                  </MenuItem>
                  <MenuItem value="todos">Todos</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Grid container spacing={2}>
            {filteredListPaginada.map((row) => {
              const displayStatus =
                row.status || (row.data_recebimento ? 'recebido' : 'pendente');
              const isFechado = row.status === 'fechado';
              return (
                <Grid item xs={12} sm={6} md={4} key={row.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      boxShadow: 3,
                      borderRadius: 2,
                      opacity: isFechado ? 0.7 : 1,
                      bgcolor: isFechado ? '#f5f5f5' : 'background.paper',
                    }}
                  >
                    <CardContent sx={{ flex: 1, p: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'start',
                          mb: 1,
                        }}
                      >
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {row.numero_os}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {isFechado && (
                            <Chip
                              icon={<Lock fontSize="small" />}
                              label="Fechado"
                              color="default"
                              size="small"
                              sx={{ bgcolor: '#9e9e9e', color: 'white' }}
                            />
                          )}
                          {!isFechado && (
                            <Chip
                              label={
                                displayStatus === 'recebido'
                                  ? 'Recebido'
                                  : displayStatus === 'em_andamento'
                                    ? 'Em andamento'
                                    : 'Pendente'
                              }
                              color={displayStatus === 'recebido' ? 'success' : 'warning'}
                              size="small"
                            />
                          )}
                        </Box>
                      </Box>

                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        👤 {clients.find((c) => c.id === row.cliente_id)?.nome || row.cliente_id}
                      </Typography>

                      <Box sx={{ mt: 2, mb: 1 }}>
                        {/* Lista de Notas Fiscais existentes */}
                        {/* Nota Fiscal antiga (campo direto) */}
                        {row.numero_nfe && (
                          <Box sx={{ mb: 1 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontWeight: 'bold', mb: 0.5, display: 'block' }}
                            >
                              Nota Principal:
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                bgcolor: row.nota_divergente ? '#fff3e0' : '#f0fdf4',
                                p: 1,
                                borderRadius: 1,
                                border: `1px solid ${row.nota_divergente ? '#ffcc80' : '#bbf7d0'}`,
                              }}
                            >
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" fontWeight="bold">
                                  NFE: {row.numero_nfe}
                                </Typography>
                                {row.valor_nfe && (
                                  <Typography variant="caption" display="block">
                                    R$ {parseFloat(row.valor_nfe).toFixed(2)}
                                  </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary">
                                  {row.data_recebimento
                                    ? formatDateTimeBr(row.data_recebimento)
                                    : ''}
                                </Typography>
                              </Box>
                              <Chip
                                label={row.nota_divergente ? 'Divergente' : 'OK'}
                                color={row.nota_divergente ? 'warning' : 'success'}
                                size="small"
                              />
                            </Box>
                          </Box>
                        )}

                        {/* Notas Fiscais adicionais (tabela notas_fiscais) */}
                        {row.notas_fiscais && row.notas_fiscais.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                              Notas Fiscais ({row.notas_fiscais.length}):
                            </Typography>
                            <Stack spacing={1.5}>
                              {row.notas_fiscais.map((nota) => {
                                const notaLocal = notasLocais[nota.id] || {
                                  numero_nfe: nota.numero_nfe || '',
                                  valor_nfe: nota.valor_nfe || '',
                                  nota_divergente: nota.nota_divergente || false,
                                };
                                return (
                                  <Paper
                                    key={nota.id}
                                    elevation={1}
                                    sx={{
                                      p: 1.5,
                                      bgcolor: notaLocal.nota_divergente ? '#fff8e1' : '#f1f8e9',
                                      border: `1px solid ${notaLocal.nota_divergente ? '#ffcc80' : '#c5e1a5'}`,
                                    }}
                                  >
                                    <Grid container spacing={1.5} alignItems="center">
                                      <Grid item xs={12} sm={3}>
                                        <TextField
                                          label="Nº NFE"
                                          size="small"
                                          fullWidth
                                          value={notaLocal.numero_nfe}
                                          onChange={(e) =>
                                            handleNotaLocalChange(
                                              nota.id,
                                              'numero_nfe',
                                              e.target.value
                                            )
                                          }
                                        />
                                      </Grid>
                                      <Grid item xs={12} sm={3}>
                                        <TextField
                                          label="Valor (R$)"
                                          size="small"
                                          fullWidth
                                          type="number"
                                          inputProps={{ step: '0.01', min: '0' }}
                                          value={notaLocal.valor_nfe}
                                          onChange={(e) =>
                                            handleNotaLocalChange(
                                              nota.id,
                                              'valor_nfe',
                                              e.target.value
                                            )
                                          }
                                        />
                                      </Grid>
                                      <Grid item xs={12} sm={3}>
                                        <FormControl fullWidth size="small">
                                          <InputLabel>Status</InputLabel>
                                          <Select
                                            value={
                                              notaLocal.nota_divergente ? 'divergente' : 'ok'
                                            }
                                            label="Status"
                                            onChange={(e) =>
                                              handleNotaLocalChange(
                                                nota.id,
                                                'nota_divergente',
                                                e.target.value === 'divergente'
                                              )
                                            }
                                          >
                                            <MenuItem value="ok">
                                              <Box
                                                sx={{
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  gap: 0.5,
                                                }}
                                              >
                                                <CheckCircle fontSize="small" color="success" />{' '}
                                                OK
                                              </Box>
                                            </MenuItem>
                                            <MenuItem value="divergente">
                                              <Box
                                                sx={{
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  gap: 0.5,
                                                }}
                                              >
                                                <Warning fontSize="small" color="warning" />{' '}
                                                Divergente
                                              </Box>
                                            </MenuItem>
                                          </Select>
                                        </FormControl>
                                      </Grid>
                                      <Grid item xs={12} sm={3}>
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                          <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={<Save />}
                                            onClick={() => handleSaveNota(row.id, nota.id)}
                                            disabled={savingNota === nota.id}
                                            sx={{ flex: 1 }}
                                          >
                                            {savingNota === nota.id ? 'Salvando...' : 'SALVAR'}
                                          </Button>
                                          <IconButton
                                            size="small"
                                            onClick={() => handleRemoveNota(row.id, nota.id)}
                                            color="error"
                                            title="Excluir nota"
                                          >
                                            <Close fontSize="small" />
                                          </IconButton>
                                        </Box>
                                      </Grid>
                                    </Grid>
                                  </Paper>
                                );
                              })}
                            </Stack>
                          </Box>
                        )}

                        {/* Formulário para adicionar nova nota (sempre visível) */}
                        <Box
                          sx={{
                            mt: 1,
                            p: 1.5,
                            bgcolor: '#f5f5f5',
                            borderRadius: 1,
                            border: '1px dashed #ccc',
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 'bold', mb: 1, display: 'block' }}
                          >
                            + Adicionar Nota Fiscal:
                          </Typography>
                          {/* Mostrar valor do pedido se disponível */}
                          {(row.valor_pedido_despesa ||
                            valorPedidoInfo[row.id] ||
                            row.valor_pedido) && (
                            <Box sx={{ mb: 1, p: 0.5, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                Valor do Pedido:{' '}
                                <b>
                                  R${' '}
                                  {parseFloat(
                                    row.valor_pedido_despesa ||
                                      valorPedidoInfo[row.id] ||
                                      row.valor_pedido
                                  ).toFixed(2)}
                                </b>
                              </Typography>
                            </Box>
                          )}
                          <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <TextField
                                placeholder="Nº NFE"
                                size="small"
                                value={notasInputs[row.id]?.nfe || ''}
                                onChange={(e) =>
                                  setNotasInputs({
                                    ...notasInputs,
                                    [row.id]: {
                                      ...(notasInputs[row.id] || {}),
                                      nfe: e.target.value,
                                    },
                                  })
                                }
                                sx={{ flex: 1 }}
                              />
                              <TextField
                                placeholder="Valor"
                                size="small"
                                type="number"
                                value={notasInputs[row.id]?.valor || ''}
                                onChange={(e) =>
                                  setNotasInputs({
                                    ...notasInputs,
                                    [row.id]: {
                                      ...(notasInputs[row.id] || {}),
                                      valor: e.target.value,
                                    },
                                  })
                                }
                                sx={{ width: 100 }}
                                InputProps={{
                                  startAdornment: <Typography sx={{ mr: 0.5 }}>R$</Typography>,
                                }}
                              />
                              <FormControl size="small" sx={{ minWidth: 110 }}>
                                <Select
                                  value={notasInputs[row.id]?.divergente ? 'divergente' : 'ok'}
                                  onChange={(e) =>
                                    setNotasInputs({
                                      ...notasInputs,
                                      [row.id]: {
                                        ...(notasInputs[row.id] || {}),
                                        divergente: e.target.value === 'divergente',
                                      },
                                    })
                                  }
                                  sx={{
                                    bgcolor: notasInputs[row.id]?.divergente
                                      ? '#ffebee'
                                      : '#e8f5e9',
                                    '& .MuiSelect-select': { py: 0.75 },
                                  }}
                                >
                                  <MenuItem value="ok">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <CheckCircle fontSize="small" color="success" />
                                      <span>OK</span>
                                    </Box>
                                  </MenuItem>
                                  <MenuItem value="divergente">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Warning fontSize="small" color="error" />
                                      <span>Divergente</span>
                                    </Box>
                                  </MenuItem>
                                </Select>
                              </FormControl>
                            </Box>
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              fullWidth
                              disabled={
                                !notasInputs[row.id]?.nfe ||
                                isManutencao ||
                                loadingAddNota === row.id
                              }
                              onClick={() => handleAddNota(row.id)}
                              startIcon={<Add />}
                            >
                              {loadingAddNota === row.id ? 'Salvando...' : '+ Adicionar Nota'}
                            </Button>
                          </Box>
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          mt: 2,
                          alignItems: 'center',
                          pt: 1,
                          borderTop: '1px solid #eee',
                        }}
                      >
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Editar">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(row)}
                              sx={{ border: '1px solid #ddd' }}
                            >
                              <Edit fontSize="small" color="primary" />
                            </IconButton>
                          </Tooltip>
                          {hasPermission('/recebimento-os', 'delete') && (
                            <Tooltip title="Excluir">
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(row.id)}
                                sx={{ border: '1px solid #ddd' }}
                              >
                                <Delete fontSize="small" color="error" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {/* Botão Fechar Pedido - só aparece se não está fechado */}
                          {!isFechado && (row.notas_fiscais?.length > 0 || row.numero_nfe) && (
                            <Tooltip title="Fechar Pedido">
                              <Button
                                size="small"
                                variant="outlined"
                                color="inherit"
                                onClick={() => handleFecharPedido(row.id)}
                                disabled={loadingFechar === row.id}
                                startIcon={<Lock fontSize="small" />}
                                sx={{ ml: 1, borderColor: '#9e9e9e', color: '#616161' }}
                              >
                                {loadingFechar === row.id ? 'Fechando...' : 'Fechar'}
                              </Button>
                            </Tooltip>
                          )}
                          {/* Botão Reabrir - só aparece se está fechado */}
                          {isFechado && (
                            <Tooltip title="Reabrir Pedido">
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                onClick={() => handleReabrirPedido(row.id)}
                                startIcon={<LockOpen fontSize="small" />}
                                sx={{ ml: 1 }}
                              >
                                Reabrir
                              </Button>
                            </Tooltip>
                          )}
                        </Box>
                        <Button
                          size="small"
                          onClick={() => toggleExpand(row.id)}
                          endIcon={expandedId === row.id ? <ExpandLess /> : <ExpandMore />}
                          sx={{ textTransform: 'none' }}
                        >
                          Detalhes
                        </Button>
                      </Box>
                    </CardContent>

                    <Collapse in={expandedId === row.id} timeout="auto" unmountOnExit>
                      <Box
                        sx={{
                          px: 2,
                          pb: 2,
                          bgcolor: '#fafafa',
                          borderTop: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Stack spacing={2} sx={{ pt: 1.5 }}>
                          {row.descricao && (
                            <Box>
                              <Typography variant="subtitle2" color="primary">
                                Dados do Pedido:
                              </Typography>
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {row.descricao}
                              </Typography>
                            </Box>
                          )}

                          {/* Anexos da Despesa vinculada */}
                          {((row.fotos_urls && row.fotos_urls.length > 0) ||
                            (row.documentos_urls && row.documentos_urls.length > 0) ||
                            (row.videos_urls && row.videos_urls.length > 0) ||
                            row.foto_path ||
                            row.pdf_path) && (
                            <Box>
                              <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                                Anexos:
                              </Typography>

                              {/* Fotos em miniatura */}
                              {((row.fotos_urls && row.fotos_urls.length > 0) ||
                                row.foto_path) && (
                                <Box sx={{ mb: 1.5 }}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', mb: 0.5 }}
                                  >
                                    Fotos:
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {row.foto_path && (
                                      <Box
                                        onClick={() =>
                                          openViewer(
                                            'foto',
                                            row.foto_path?.startsWith('/')
                                              ? row.foto_path
                                              : `/${row.foto_path}`,
                                            'Foto'
                                          )
                                        }
                                        sx={{
                                          width: 60,
                                          height: 60,
                                          borderRadius: 1,
                                          overflow: 'hidden',
                                          cursor: 'pointer',
                                          border: '2px solid #ddd',
                                          '&:hover': { borderColor: 'primary.main' },
                                        }}
                                      >
                                        <img
                                          src={
                                            row.foto_path?.startsWith('/')
                                              ? row.foto_path
                                              : `/${row.foto_path}`
                                          }
                                          alt="Foto"
                                          style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                          }}
                                        />
                                      </Box>
                                    )}
                                    {(row.fotos_urls || []).map((url, idx) => (
                                      <Box
                                        key={idx}
                                        onClick={() => openViewer('foto', url, `Foto ${idx + 1}`)}
                                        sx={{
                                          width: 60,
                                          height: 60,
                                          borderRadius: 1,
                                          overflow: 'hidden',
                                          cursor: 'pointer',
                                          border: '2px solid #ddd',
                                          '&:hover': { borderColor: 'primary.main' },
                                        }}
                                      >
                                        <img
                                          src={url}
                                          alt={`Foto ${idx + 1}`}
                                          style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                          }}
                                        />
                                      </Box>
                                    ))}
                                  </Box>
                                </Box>
                              )}

                              {/* PDFs */}
                              {((row.documentos_urls && row.documentos_urls.length > 0) ||
                                row.pdf_path) && (
                                <Box sx={{ mb: 1.5 }}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', mb: 0.5 }}
                                  >
                                    Documentos:
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {row.pdf_path && (
                                      <Chip
                                        icon={<PictureAsPdf />}
                                        label="PDF"
                                        size="small"
                                        onClick={() =>
                                          openViewer(
                                            'pdf',
                                            row.pdf_path?.startsWith('/')
                                              ? row.pdf_path
                                              : `/${row.pdf_path}`,
                                            'PDF'
                                          )
                                        }
                                        sx={{ cursor: 'pointer' }}
                                      />
                                    )}
                                    {(row.documentos_urls || []).map((doc, idx) => (
                                      <Chip
                                        key={idx}
                                        icon={<Description />}
                                        label={doc.name || `Doc ${idx + 1}`}
                                        size="small"
                                        onClick={() =>
                                          openViewer(
                                            'pdf',
                                            doc.url || doc,
                                            doc.name || `Documento ${idx + 1}`
                                          )
                                        }
                                        sx={{ cursor: 'pointer' }}
                                      />
                                    ))}
                                  </Box>
                                </Box>
                              )}

                              {/* Vídeos */}
                              {row.videos_urls && row.videos_urls.length > 0 && (
                                <Box>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', mb: 0.5 }}
                                  >
                                    Vídeos:
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {row.videos_urls.map((url, idx) => (
                                      <Chip
                                        key={idx}
                                        icon={<VideoLibrary />}
                                        label={`Vídeo ${idx + 1}`}
                                        size="small"
                                        onClick={() =>
                                          openViewer('video', url, `Vídeo ${idx + 1}`)
                                        }
                                        sx={{ cursor: 'pointer' }}
                                      />
                                    ))}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          )}

                          <Box>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1 }}
                            >
                              Histórico
                            </Typography>
                            {loadingHistory && !historyData[row.id] ? (
                              <Typography variant="caption">Carregando...</Typography>
                            ) : (
                              <Stack spacing={1}>
                                {(historyData[row.id] || []).map((hist) => (
                                  <Paper
                                    key={hist.id}
                                    sx={{ p: 1, border: '1px solid #eee', boxShadow: 'none' }}
                                  >
                                    <Typography
                                      variant="caption"
                                      display="block"
                                      color="text.secondary"
                                    >
                                      {formatDateTimeBr(hist.data_hora)} -{' '}
                                      <b>{hist.usuario_nome}</b>
                                    </Typography>
                                    <Typography variant="body2">
                                      {hist.acao === 'CREATE'
                                        ? 'Criou o pedido'
                                        : hist.acao === 'UPDATE'
                                          ? 'Atualizou o pedido'
                                          : 'Excluiu'}
                                    </Typography>
                                    {hist.acao === 'UPDATE' && hist.valores_novos && (
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          display: 'block',
                                          mt: 0.5,
                                          color: '#666',
                                          fontStyle: 'italic',
                                        }}
                                      >
                                        {hist.valores_novos}
                                      </Typography>
                                    )}
                                  </Paper>
                                ))}
                                {(!historyData[row.id] || historyData[row.id].length === 0) && (
                                  <Typography variant="body2" color="text.secondary">
                                    Nenhuma ocorrência registrada.
                                  </Typography>
                                )}
                              </Stack>
                            )}
                          </Box>
                        </Stack>
                      </Box>
                    </Collapse>
                  </Card>
                </Grid>
              );
            })}
            {!filterClienteId && osList.length === 0 && (
              <Grid item xs={12}>
                <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                  <Typography variant="body1">
                    Selecione um cliente para carregar as OS.
                  </Typography>
                </Box>
              </Grid>
            )}
            {filterClienteId && filteredList.length === 0 && (
              <Grid item xs={12}>
                <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                  Nenhuma OS encontrada.
                </Box>
              </Grid>
            )}
          </Grid>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 3, gap: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {filteredList.length} registro{filteredList.length !== 1 ? 's' : ''} — página {page} de {totalPages}
              </Typography>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, v) => setPage(v)}
                color="primary"
                size="small"
                siblingCount={1}
              />
            </Box>
          )}
        </Box>
      )}

      {/* Modal Visualizador de Mídia */}
      <Dialog open={viewerOpen} onClose={closeViewer} maxWidth="lg" fullWidth>
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          {viewerTitle || 'Visualizador'}
          <IconButton onClick={closeViewer}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 400,
            bgcolor: '#000',
          }}
        >
          {viewerType === 'foto' && (
            <img
              src={viewerUrl}
              alt="Visualização"
              style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
            />
          )}
          {viewerType === 'pdf' && (
            <iframe
              src={viewerUrl}
              title="PDF Viewer"
              style={{ width: '100%', height: '70vh', border: 'none' }}
            />
          )}
          {viewerType === 'video' && (
            <video controls style={{ maxWidth: '100%', maxHeight: '70vh' }}>
              <source src={viewerUrl} type="video/mp4" />
              Seu navegador não suporta vídeos.
            </video>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmação de Divergência */}
      <Dialog open={confirmDialogOpen} onClose={handleCancelDivergente} maxWidth="sm">
        <DialogTitle sx={{ bgcolor: '#fff3e0' }}>⚠️ Valores Divergentes</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body1" gutterBottom>
            Os valores informados não conferem:
          </Typography>
          <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, my: 2 }}>
            <Typography variant="body2">
              <strong>Valor do Pedido:</strong> R${' '}
              {pendingReceber?.valor_pedido?.toFixed(2) || '0.00'}
            </Typography>
            <Typography variant="body2">
              <strong>Valor da NFE:</strong> R$ {pendingReceber?.valor_nfe?.toFixed(2) || '0.00'}
            </Typography>
            <Typography variant="body2" color="error" sx={{ mt: 1, fontWeight: 'bold' }}>
              Diferença: R${' '}
              {pendingReceber
                ? Math.abs(pendingReceber.valor_nfe - pendingReceber.valor_pedido).toFixed(2)
                : '0.00'}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Deseja salvar mesmo assim? A nota será marcada como <strong>DIVERGENTE</strong>.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCancelDivergente} variant="outlined">
            Cancelar
          </Button>
          <Button onClick={handleConfirmDivergenteMulti} variant="contained" color="warning">
            Salvar como Divergente
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
